import React from 'react';
import vars from '@variables';
import styled from '@emotion/styled/macro';
import Layout from '@layouts/layout';
import SectionLayout from '@layouts/section-layout';
import FlexibleContentHandler from '../design-system/src/components/flexible-content-handler/flexible-content-handler';
import TagList from '@components/tag-list/tag-list';
import ErrorLayout from '@layouts/error-layout';
import Masthead from '@components/masthead/masthead';
import { graphql } from 'gatsby';
import { getPageColour } from '../config/cms-mapping-functions';
import { mergeSubPages } from '../utils/custom-page-functions';
import { getFooterData } from '../utils/translation-functions';
import { createNavbarLocaleLinks } from '../utils/generate-url-functions';
import { validateStructurePageData } from '../utils/validate-cms-data-functions';
import { IndentWrapper } from '@components/indent-wrapper/indent-wrapper';
import { Paywall } from '@components/paywall/paywall';
import {
  transformFlexibleContent,
  transformSocialStaticImages,
  getHrefLangUrls,
} from '../utils/transform-data-functions';

const StyledWrapper = styled.div`
  .main-container {
    display: flex;
    max-width: ${vars.mainMaxWidth};
    justify-content: center;
    margin: 0 auto;

    .main-content-container {
      min-width: ${vars.contentMinWidth};
      width: calc(
        ${vars.contentMaxWidth} + calc(2 * ${vars.standardColumnGap})
      );
      padding: ${vars.spacingL} ${vars.mainContentContainerPaddingMobile};

      @media screen and (min-width: ${vars.breakPointTenColumn}) {
        padding: ${vars.spacingL} ${vars.mainContentContainerPaddingTenColumn};
      }

      @media screen and (min-width: ${vars.breakPointTablet}) {
        padding: ${vars.spacingL} ${vars.mainContentContainerPaddingTenColumn};
      }
      @media screen and (min-width: ${vars.breakPointTwelveColumn}) {
        padding: ${vars.spacingL} ${vars.spacingL} 12rem ${vars.spacingL};
      }
    }
  }
`;

export const query = graphql`
  query StructureQuery(
    $structureId: String
    $structureContentfulId: String
    $subPageId: String
    $subPageContentfulId: String
    $podcastSlugs: [String]
    $locale: String
  ) {
    structure: contentfulStructure(id: { eq: $structureId }) {
      id
      contentful_id
      node_locale
      enabled
      type
      title
      slug
      colour
      subPages {
        id
        enabled
        node_locale
        slug
        tabTitle
      }
    }
    subPage: contentfulSubPage(id: { eq: $subPageId }, enabled: { eq: true }) {
      ...SubPageFields
    }
    allCustomSubPage(
      filter: {
        parentStructureContentfulId: { eq: $structureContentfulId }
        node_locale: { eq: $locale }
      }
    ) {
      nodes {
        id
        index
        parentStructureContentfulId
        slug
        tabTitle
        isExternalLink
      }
    }
    allSimplecastPodcastEpisode(filter: { slug: { in: $podcastSlugs } }) {
      nodes {
        id
        slug
      }
    }
    pageDefaultImages: contentfulSubPage(
      contentful_id: { eq: $subPageContentfulId }
      node_locale: { eq: "en-GB" }
    ) {
      mastheadImage {
        gatsbyImageData(
          placeholder: BLURRED
          formats: [AUTO, AVIF, WEBP]
          quality: 80
        )
        description
        file {
          url
          contentType
        }
      }
      socialShareImage {
        description
        file {
          url
          contentType
        }
      }
    }
    contentfulGlossaryManager(enabled: { eq: true }) {
      termsForTooltip {
        label
        definition {
          definition
        }
        definitionImage {
          gatsbyImageData(
            placeholder: BLURRED
            formats: [AUTO, AVIF, WEBP]
            quality: 80
          )
          description
          file {
            url
            contentType
          }
        }
        linkType
        externalLink
        internalLink {
          ...StandardPageReference
          ...SubPageReference
          ...CircularExamplePageReference
          ...ResourcePageReference
          ...WebflowPageReference
        }
      }
    }
  }
`;

const Structure = ({ data, location, pageContext }) => {
  const structure = data.structure;
  const subPage = data.subPage;
  const pageUrl = location.pathname;
  const canonicalSelfReference = `${vars.baseDomain}${location.pathname}`;
  const pageValidation = validateStructurePageData(structure, subPage);

  const {
    mastheadData,
    subPageLocales,
    localeLinks,
    locale,
    enabledLocaleCodes,
    staticTranslations,
    structurePath,
    globals,
    topics: transformedTags,
  } = pageContext;

  const footerData = getFooterData(locale);

  if (pageValidation.isValid) {
    const pageDefaultImages = data.pageDefaultImages;
    const customSubPages = data.allCustomSubPage.nodes;
    const pageColour = getPageColour(structure.colour);
    const secondaryNavTabs = mergeSubPages(
      structure.subPages,
      customSubPages,
      locale
    ).map(navSubPage => {
      const isExternal = navSubPage.isExternalLink;
      return {
        title: navSubPage.tabTitle,
        url: isExternal
          ? navSubPage.slug
          : `${structurePath}/${navSubPage.slug}`,
        active: subPage.id === navSubPage.id ? true : false,
        isExternalLink: isExternal,
      };
    });

    const secondaryNavData = {
      title: structure.title,
      tabs: secondaryNavTabs,
      colour: pageColour,
    };

    const hreflangUrls = getHrefLangUrls(
      location.pathname,
      locale,
      localeLinks
    );

    const navbarLocaleLinks = createNavbarLocaleLinks(
      subPageLocales,
      enabledLocaleCodes
    );

    // Podcast data
    const simplecastData = data.allSimplecastPodcastEpisode?.nodes;

    // Tooltip data
    const { termsForTooltip } = data.contentfulGlossaryManager;

    const rawTooltipData = {
      termsForTooltip,
      shouldDisplayTextTooltips: subPage.shouldDisplayTextTooltips,
      usedTooltipKeywords: [],
    };

    // transform flexible content into consistent format for front end
    const transformedFlexibleContent = transformFlexibleContent({
      flexibleContent: subPage.flexibleContent,
      localeCode: locale,
      staticTranslations,
      pageColour,
      simplecastData,
      rawTooltipData,
      currentEntryId: subPage.id,
    });

    const metaData = {
      title: subPage.title,
      metaTitle:
        subPage.metaTitle || subPage.listingTitle || subPage.mastheadTitle,
      metaDescription: subPage.metaDescription || subPage.listingDescription,
      metaImage: transformSocialStaticImages(
        subPage.socialShareImage,
        mastheadData.mastheadBackgroundImage,
        pageDefaultImages
      ),
      socialCardTitle: subPage.listingTitle || subPage.mastheadTitle,
      socialCardDescription:
        subPage.listingDescription || subPage.metaDescription,
      canonicalLink: subPage.canonicalLink
        ? subPage.canonicalLink
        : canonicalSelfReference,
      localeCode: locale,
    };

    const gtmDataLayerVars = {
      pageId: subPage.contentful_id,
      event: 'Page View',
      pageType: 'sub page',
      lastUpdated: subPage.updatedAt,
      topics: transformedTags,
      geography: subPage.geography,
    };

    const hasPaywall = subPage?.earlyAccessCode?.earlyAccessCode;

    return (
      <Layout
        secondaryNavData={secondaryNavData}
        footerData={footerData}
        metaData={metaData}
        gtmDataLayerVars={gtmDataLayerVars}
        hreflangUrls={hreflangUrls}
        globals={globals ? globals : null}
        pageUrl={pageUrl ? pageUrl : null}
        navbarLocaleLinks={navbarLocaleLinks}
      >
        <Masthead hasSecondaryNav={true} />

        <StyledWrapper>
          <div className="main-container">
            <div className="main-content-container" role="main">
              <IndentWrapper
                openingParagraph={
                  subPage.openingParagraph
                    ? subPage.openingParagraph.openingParagraph
                    : null
                }
                mediaSrc={subPage.openingVideoUrl || null}
                pageColour={pageColour}
              />
              {transformedFlexibleContent && (
                <FlexibleContentHandler data={transformedFlexibleContent} />
              )}

              <div
                id="hotjar_feedback_widget"
                style={{ paddingTop: '2.4rem' }}
              />

              {transformedTags && (
                <SectionLayout>
                  <TagList tags={transformedTags} />
                </SectionLayout>
              )}
            </div>
          </div>
        </StyledWrapper>
        {hasPaywall && (
          <Paywall
            earlyAccessCode={subPage?.earlyAccessCode?.earlyAccessCode}
            publicLiveDate={subPage.publicLiveDate}
          />
        )}
      </Layout>
    );
  } else {
    return (
      <ErrorLayout footerData={footerData} errors={pageValidation.errors} />
    );
  }
};

export default Structure;
